/**
 * Smoke grenades on screen (`world.smokes`, 04 §6): each cloud is one cluster of low-poly
 * puffs scaled to the sim's radius - so what you see is what blocks sight - and thinned with
 * the world-anchored dither as it ages (05 §8.1: no blending anywhere). Round the ranger the
 * cloud is thinned further, or the player could not see himself inside his own smoke.
 */

import { type BufferGeometry, type Color, type Material, Mesh, type Scene, type Texture, Vector3 } from 'three';
import { Pcg32 } from '../../sim/rng';
import { type SmokeCloud, TICK_RATE, type World } from '../../sim/types';
import { type PixelCamera, TARGET_H, TARGET_W } from '../camera/PixelCamera';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import { createDitherMaterial, ditherStep } from '../materials/dither';
import type { Palette } from '../palette';

const TAU = Math.PI * 2;
/** A fresh cloud keeps this share of its pixels: dense, but never a solid lump. */
export const SMOKE_DENSE = 0.75;
/** ...and the last wisps this much, before the sim takes the cloud away. */
export const SMOKE_THIN = 0.125;
/** It thins over its last seconds, in the dither's sixteen steps. */
const THIN_TICKS = 6 * TICK_RATE;
/** The cluster is modelled one metre across; smoke lies lower than it is wide. */
const HEIGHT_SCALE = 0.62;
/** Size changes in steps this big, metres, so the low-poly edge jumps rather than crawls. */
const RADIUS_STEP = 0.125;
/** The churn: the whole cluster turns one notch at this rate. */
const CHURN_HZ = 2;
const CHURN_NOTCH = TAU / 24;
/** Metres round the ranger's chest, on screen, kept thin enough to see him through. */
const HOLE_METRES = 2.0;

/** `smoke.*` once the palette has that ramp; until then the greys and off-white it does have. */
function tone(p: Palette, ref: string, fallback: string): Color {
  const ramp = ref.split('.')[0] ?? '';
  return p.color(p.ramps[ramp] ? ref : fallback);
}

/** One cloud, a metre in radius, sitting on y = 0: a heart, a ring of lumps round it, and a cap. */
export function smokeCluster(p: Palette, rng: Pcg32): MeshBuilder {
  const mb = new MeshBuilder();
  const shade = tone(p, 'smoke.0', 'metal.mid');
  const body = tone(p, 'smoke.1', 'metal.hi');
  const light = tone(p, 'smoke.2', 'paper.0');
  const heart = { jitter: 0.16, rng, flatBottom: true, topColor: light };
  mb.blob(0.62, 0.52, 0.62, body, place(0, 0, 0, rng.range(0, TAU)), heart);
  const lumps = 6;
  for (let i = 0; i < lumps; i++) {
    const a = (i / lumps) * TAU + rng.range(-0.3, 0.3);
    const d = rng.range(0.42, 0.58);
    const r = rng.range(0.34, 0.44);
    const at = place(Math.sin(a) * d, 0, Math.cos(a) * d, rng.range(0, TAU));
    const dark = i % 2 === 0;
    const lump = { jitter: 0.18, rng, flatBottom: true, topColor: dark ? body : light };
    mb.blob(r, r * rng.range(0.6, 0.85), r, dark ? shade : body, at, lump);
  }
  mb.blob(0.4, 0.3, 0.4, light, place(0.08, 0.4, -0.05), { jitter: 0.15, rng });
  return mb;
}

/** Share of pixels a cloud keeps with `ticksLeft` to live. */
export function smokeCoverage(ticksLeft: number): number {
  const k = Math.max(0, Math.min(1, ticksLeft / THIN_TICKS));
  return ditherStep(SMOKE_THIN + (SMOKE_DENSE - SMOKE_THIN) * k);
}

interface CloudView {
  mesh: Mesh;
  material: Material;
  coverage: { value: number };
}

export class SmokeView {
  private readonly geometry: BufferGeometry;
  private readonly views = new Map<number, CloudView>();
  /** Views whose cloud has gone, kept for the next one: a material each, but one shader between them. */
  private readonly spare: CloudView[] = [];
  private readonly hole = { value: new Vector3() };
  private readonly scratch = new Vector3();

  constructor(
    private readonly scene: Scene,
    palette: Palette,
    private readonly gradient: Texture,
    private readonly groundY: (x: number, z: number) => number,
  ) {
    this.geometry = smokeCluster(palette, new Pcg32(0x5a0c, 17)).build();
  }

  /** Clouds currently drawn (for tests and the debug overlay). */
  get count(): number {
    return this.views.size;
  }

  private view(cloud: SmokeCloud): CloudView {
    let v = this.views.get(cloud.id) ?? this.spare.pop();
    if (!v) {
      const coverage = { value: SMOKE_DENSE };
      const material = createDitherMaterial({ gradientMap: this.gradient, coverage, hole: this.hole });
      const mesh = new Mesh(this.geometry, material);
      // Smoke takes shadow (it is lit like everything else) but throws none: the shadow pass cannot dither.
      mesh.receiveShadow = true;
      mesh.name = 'smoke';
      v = { mesh, material, coverage };
    }
    if (!this.views.has(cloud.id)) {
      this.views.set(cloud.id, v);
      this.scene.add(v.mesh);
      v.mesh.position.set(cloud.x, this.groundY(cloud.x, cloud.z), cloud.z);
    }
    return v;
  }

  update(world: World, time: number, camera: PixelCamera): void {
    if (world.smokes.length === 0 && this.views.size === 0) return;
    const r = world.ranger;
    let inside = false;
    for (const cloud of world.smokes) {
      const v = this.view(cloud);
      const radius = Math.max(RADIUS_STEP, Math.round(cloud.r / RADIUS_STEP) * RADIUS_STEP);
      v.mesh.scale.set(radius, radius * HEIGHT_SCALE, radius);
      // Each cloud starts at its own angle and churns in notches, on the same beat as the others.
      v.mesh.rotation.y = cloud.id * 2.4 + Math.floor(time * CHURN_HZ) * CHURN_NOTCH;
      v.coverage.value = smokeCoverage(cloud.ticksLeft);
      if (Math.hypot(r.x - cloud.x, r.z - cloud.z) < cloud.r + HOLE_METRES) inside = true;
    }
    for (const [id, v] of this.views) {
      if (world.smokes.some((c) => c.id === id)) continue;
      this.scene.remove(v.mesh);
      this.views.delete(id);
      this.spare.push(v);
    }
    // The hole follows the ranger's chest in scene-target pixels; radius 0 switches it off.
    if (inside) {
      this.scratch.set(r.x, this.groundY(r.x, r.z) + 0.9, r.z).project(camera.camera);
      const px = (this.scratch.x * 0.5 + 0.5) * TARGET_W;
      const py = (this.scratch.y * 0.5 + 0.5) * TARGET_H;
      this.hole.value.set(px, py, HOLE_METRES * camera.pixelsPerMetre);
    } else this.hole.value.set(0, 0, 0);
  }

  dispose(): void {
    for (const v of [...this.views.values(), ...this.spare]) {
      this.scene.remove(v.mesh);
      v.material.dispose();
    }
    this.views.clear();
    this.spare.length = 0;
    this.geometry.dispose();
  }
}
